import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit3, Trash2, ToggleLeft, ToggleRight, X, MoreVertical, RefreshCw, Ban, Search } from 'lucide-react';
import Field from '../components/Field';
import { useToast } from '../context/ToastContext';
import { api } from '../utils/api';
import Pagination from '../components/Pagination';
import { motion, AnimatePresence } from 'motion/react';
import { downloadCSV } from '../utils/csv';


const mockPayments = [
  { id: 'pay_001', user: 'john@mail.com', video: 'Trading Basics Vol.1', amount: 1999, status: 'success', date: '2025-07-12' },
  { id: 'pay_002', user: 'jane@mail.com', video: 'Options Mastery', amount: 2999, status: 'success', date: '2025-07-11' },
  { id: 'pay_003', user: 'alex@mail.com', video: 'Advanced Charts', amount: 1499, status: 'failed', date: '2025-07-10' },
  { id: 'pay_004', user: 'sara@mail.com', video: 'Stock Strategies', amount: 2499, status: 'success', date: '2025-07-09' },
];

export function AdminPayments() {
  const { toast } = useToast();
  const [payments, setPayments] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchPayments = (page = 1) => {
    setLoading(true);
    api.get(`/payments/admin?page=${page}&limit=5&search=${search}`)
      .then((res: any) => {
        setPayments(res.data || []);
        setPagination(res.pagination);
      })
      .catch(() => setPayments([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPayments(1);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => fetchPayments(1), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const handleExport = async () => {
    try {
      const res = await api.get('/payments/export');
      downloadCSV(res.data, 'payments_export');
    } catch {
      toast('Export failed', 'error');
    }
  };

  const total = payments.filter(p => p.status === 'captured').reduce((s, p) => s + Number(p.amount), 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-black text-[var(--color-text)] tracking-tight">Payment Management</h1>
          <p className="text-sm text-[var(--color-muted)]">Total revenue: <span className="font-bold text-[var(--color-accent)]">₹{total.toLocaleString()}</span></p>
        </div>
        <button
          onClick={handleExport}
          className="btn btn-ghost btn-sm self-start sm:self-auto"
        >
          Export CSV
        </button>
      </div>

      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
          style={{ color: 'var(--color-muted)' }} />
        <input className="input !pl-[30px]" placeholder="Search payments…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="card">
        <div className="p-4 sm:p-0">
          {/* Mobile card layout */}
          <div className="sm:hidden space-y-3">
            {loading ? (
              <div className="text-center text-sm text-[var(--color-muted)] py-10">Loading payments…</div>
            ) : payments.map(p => (
              <div key={p.id} className="bg-[var(--glass-bg)] rounded-xl p-3 border border-[var(--glass-border)]">
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-[var(--color-text)] truncate">{p.email}</p>
                    <p className="text-[10px] text-[var(--color-muted)] truncate">{p.video_title}</p>
                    <p className="text-[10px] text-[var(--color-muted)] font-mono">{p.razorpay_payment_id || p.id}</p>
                  </div>
                  <span className={`badge ${p.status === 'captured' ? 'badge-green' : 'badge-red'} ml-2`}>
                    {p.status === 'captured' ? '● Success' : `● ${p.status}`}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-[var(--color-accent)]">₹{p.amount.toLocaleString()}</p>
                    <p className="text-[10px] text-[var(--color-muted)]">{new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            ))}
            {!loading && payments.length === 0 && (
              <div className="text-center text-[var(--color-muted)] py-8 text-sm">No payments found</div>
            )}
          </div>

          {/* Desktop table layout */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Payment ID</th>
                  <th>User</th>
                  <th>Video</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center text-sm text-[var(--color-muted)] py-10">Loading payments…</td></tr>
                ) : payments.map(p => (
                  <tr key={p.id}>
                    <td className="font-mono text-[var(--color-muted)]">{p.razorpay_payment_id || p.id}</td>
                    <td className="max-w-[140px] truncate font-medium">{p.email}</td>
                    <td className="max-w-[160px] truncate text-[var(--color-muted)]">{p.video_title}</td>
                    <td className="font-bold text-[var(--color-accent)]">₹{p.amount.toLocaleString()}</td>
                    <td><span className={`badge ${p.status === 'captured' ? 'badge-green' : 'badge-red'}`}>
                      {p.status === 'captured' ? '● Success' : `● ${p.status}`}
                    </span></td>
                    <td className="text-[var(--color-muted)]">{new Date(p.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {!loading && payments.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-[var(--color-muted)] py-10">No payments found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        onPageChange={fetchPayments}
      />
    </div>
  );
}

export function AdminSubscriptions() {
  const { toast } = useToast();
  const [subs, setSubs] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [users, setUsers] = useState<any[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGrant, setShowGrant] = useState(false);
  const [search, setSearch] = useState('');

  // Grant form — user comes from Add New User data; courses come from Video Management
  const [grantForm, setGrantForm] = useState({
    user_id: '',
    video_ids: [] as string[],
    validity_days: '180',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const fetchSubs = (page = 1) => {
    setLoading(true);
    api.get(`/admin/subscriptions?page=${page}&limit=5&search=${search}`)
      .then((res: any) => {
        setSubs(res.data || []);
        setPagination(res.pagination);
      })
      .catch(() => setSubs([]))
      .finally(() => setLoading(false));
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/admin/subscriptions/export');
      downloadCSV(res.data, 'subscriptions_export');
    } catch {
      toast('Export failed', 'error');
    }
  };

  const fetchRefs = () => {
    Promise.all([
      api.get('/admin/users?limit=50'), // Get all for dropdown
      api.get('/videos?limit=50')
    ]).then(([u, v]) => {
      setUsers(u.data || []);
      setVideos(v.data || []);
    }).catch(() => { });
  };

  useEffect(() => {
    fetchSubs(1);
    fetchRefs();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => fetchSubs(1), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const toggleVideo = (id: string) =>
    setGrantForm(p => ({
      ...p,
      video_ids: p.video_ids.includes(id)
        ? p.video_ids.filter(x => x !== id)
        : [...p.video_ids, id],
    }));

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!grantForm.user_id) errs.user_id = 'Select a user.';
    if (grantForm.video_ids.length === 0) errs.video_ids = 'Select at least one course.';
    if (!grantForm.validity_days || +grantForm.validity_days < 1) errs.validity_days = 'Enter valid days.';

    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    try {
      await Promise.all(
        grantForm.video_ids.map(vid =>
          api.post('/admin/subscriptions/grant', {
            user_id: +grantForm.user_id, video_id: +vid, validity_days: +grantForm.validity_days,
          })
        )
      );
      toast(`Access granted for ${grantForm.video_ids.length} course(s)!`, 'success');
      setShowGrant(false);
      setGrantForm({ user_id: '', video_ids: [], validity_days: '30' });
      setDropdownOpen(false);
      fetchSubs(1);
    } catch { toast('Grant failed. Try again.', 'error'); }
  };

  const handleRevoke = async (id: number) => {
    try {
      await api.patch(`/admin/subscriptions/${id}/revoke`);
      toast('Subscription revoked.', 'info');
      fetchSubs(pagination.page);
    } catch { toast('Failed to revoke.', 'error'); }
  };

  const handleReactivate = async (id: number) => {
    try {
      await api.patch(`/admin/subscriptions/${id}/reactivate`);
      toast('Subscription reactivated!', 'success');
      fetchSubs(pagination.page);
    } catch { toast('Failed to reactivate.', 'error'); }
  };

  const selectedUser = users.find(u => String(u.id) === grantForm.user_id);
  const selectedVideos = videos.filter(v => grantForm.video_ids.includes(String(v.id)));

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex-1">
          <h1 className="text-xl font-black text-[var(--color-text)] tracking-tight">Subscription Management</h1>
          <p className="text-sm text-[var(--color-muted)]">{pagination.total} total subscriptions</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} className="btn btn-ghost btn-sm">
            Export CSV
          </button>
          <button onClick={() => setShowGrant(s => !s)} className="btn btn-primary btn-sm self-start sm:self-auto">
            <Plus className="w-3.5 h-3.5" /> Grant Access
          </button>
        </div>
      </div>

      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
          style={{ color: 'var(--color-muted)' }} />
        <input
          className="input !pl-[30px]"
          placeholder="Search subscriptions…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {showGrant && (
        <div className="card p-5 sm:p-6 border border-[var(--color-accent)]">
          <h2 className="text-sm font-bold text-[var(--color-text)] mb-5 flex items-center gap-2">
            <Plus className="w-4 h-4 text-[var(--color-accent)]" /> Manually Grant Subscription
          </h2>
          <form onSubmit={handleGrant} className="space-y-5">
            {/* ── User selector ── */}
            <Field label="Select User (from User Management)" error={errors.user_id} required>
              <select
                className={`input ${errors.user_id ? 'border-red-400' : ''}`}
                value={grantForm.user_id}
                onChange={e => setGrantForm(p => ({ ...p, user_id: e.target.value }))}
              >
                <option value="">Choose a registered user…</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name ? `${u.name} — ${u.email}` : u.email}
                    {u.phone ? ` · ${u.phone}` : ''}
                  </option>
                ))}
              </select>
            </Field>

            {selectedUser && (
              <p className="mt-[-12px] text-[11px] text-[var(--color-accent)] font-semibold">
                Selected: {selectedUser.name || selectedUser.email}
                {selectedUser.phone ? ` · ${selectedUser.phone}` : ''}
              </p>
            )}

            {/* ── Course multi-select ── */}
            <Field label="Select Courses (from Video Management)" error={errors.video_ids} required>
              <div className="relative">
                <button
                  ref={buttonRef}
                  type="button"
                  onClick={() => setDropdownOpen(o => !o)}
                  className={`input flex items-center justify-between text-left cursor-pointer ${errors.video_ids ? 'border-red-400' : ''}`}
                  style={{ color: grantForm.video_ids.length ? 'var(--color-text)' : 'var(--color-muted)' }}
                >
                  <span className="truncate">
                    {grantForm.video_ids.length === 0
                      ? 'Select one or more courses…'
                      : `${grantForm.video_ids.length} course${grantForm.video_ids.length > 1 ? 's' : ''} selected`}
                  </span>
                  <svg className={`w-4 h-4 shrink-0 ml-2 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown panel */}
                {dropdownOpen && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-50 shadow-2xl rounded-xl border border-[var(--glass-border)]"
                    style={{
                      background: 'var(--color-bg)',
                      top: '100%',
                      left: '0',
                      right: '0',
                      marginTop: '4px',
                      maxHeight: '320px'
                    }}>
                    {videos.length === 0 ? (
                      <p className="px-4 py-3 text-xs text-[var(--color-muted)]">
                        No videos found. Add videos in Video Management first.
                      </p>
                    ) : (
                      <div className="overflow-y-auto" style={{ maxHeight: '160px' }}>
                        {videos.map(v => {
                          const checked = grantForm.video_ids.includes(String(v.id));
                          return (
                            <label
                              key={v.id}
                              className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[var(--glass-bg)] transition-colors border-b border-[var(--glass-border)] last:border-0"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleVideo(String(v.id))}
                                className="w-4 h-4 rounded accent-[#5f9c4c] shrink-0"
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-[var(--color-text)] truncate">{v.title}</p>
                                <p className="text-[11px] text-[var(--color-muted)]">
                                  {v.category} · ₹{v.price}
                                </p>
                              </div>
                              {checked && (
                                <span className="ml-auto badge badge-green shrink-0">Selected</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {grantForm.video_ids.length > 0 && (
                      <div className="px-4 py-2 bg-[var(--glass-bg)] flex items-center justify-between border-t border-[var(--glass-border)]">
                        <span className="text-xs font-bold text-[var(--color-accent)]">
                          {grantForm.video_ids.length} selected
                        </span>
                        <button type="button"
                          onClick={() => { setGrantForm(p => ({ ...p, video_ids: [] })); }}
                          className="text-xs text-red-500 font-semibold hover:underline">
                          Clear all
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Selected course chips */}
              {selectedVideos.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {selectedVideos.map(v => (
                    <span key={v.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold"
                      style={{ background: 'rgba(95,156,76,0.12)', color: 'var(--color-accent)' }}>
                      {v.title}
                      <button type="button" onClick={() => toggleVideo(String(v.id))}
                        className="opacity-60 hover:opacity-100 transition-opacity ml-0.5">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </Field>

            {/* Validity */}
            <div className="max-w-xs">
              <Field label="Validity (days)" error={errors.validity_days} required>
                <input
                  type="number" min="1" className={`input ${errors.validity_days ? 'border-red-400' : ''}`}
                  value={grantForm.validity_days}
                  onChange={e => setGrantForm(p => ({ ...p, validity_days: e.target.value }))}
                  placeholder="180"
                />
              </Field>
            </div>

            <div className="flex gap-3 pt-1">
              <button type="submit" className="btn btn-primary btn-sm">
                Grant Access
              </button>
              <button type="button" onClick={() => { setShowGrant(false); setDropdownOpen(false); }} className="btn btn-ghost btn-sm">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Subscriptions table */}
      <div className="card">
        {loading ? (
          <div className="py-12 text-center text-sm text-[var(--color-muted)]">Loading…</div>
        ) : (
          <div className="p-4 sm:p-0">
            {/* Mobile card layout */}
            <div className="sm:hidden space-y-3">
              {subs.map((s: any) => (
                <div key={s.id} className="bg-[var(--glass-bg)] rounded-xl p-3 border border-[var(--glass-border)]">
                  <div className="flex items-start justify-between mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-[var(--color-text)]">{s.name || s.email}</p>
                      <p className="text-[10px] text-[var(--color-muted)] truncate">{s.email}</p>
                      <p className="text-[10px] text-[var(--color-muted)] truncate">{s.video_title}</p>
                    </div>
                    <span className={`badge ${s.status === 'active' ? 'badge-green' : 'badge-red'} ml-2`}>
                      {s.status === 'active' ? '● Active' : s.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[var(--color-muted)]">
                        Granted: {new Date(s.purchase_date).toLocaleDateString()}
                      </span>
                      <span className="text-[10px] text-[var(--color-muted)]">
                        Expires: {new Date(s.expiry_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    {s.status === 'active' ? (
                      <button
                        onClick={() => handleRevoke(s.id)}
                        className="btn btn-sm btn-danger flex items-center gap-1.5"
                      >
                        <Ban className="w-3 h-3" /> Revoke
                      </button>
                    ) : (
                      <button
                        onClick={() => handleReactivate(s.id)}
                        className="btn btn-sm btn-ghost flex items-center gap-1.5 border-[var(--color-accent)] text-[var(--color-accent)] hover:bg-[var(--glass-bg)]"
                      >
                        <RefreshCw className="w-3 h-3" /> Reactivate
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {subs.length === 0 && (
                <div className="text-center text-[var(--color-muted)] py-8 text-sm">No subscriptions yet.</div>
              )}
            </div>

            {/* Desktop table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="tbl">
                <thead>
                  <tr><th>User</th><th>Course</th><th>Granted</th><th>Expires</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {subs.map((s: any) => (
                    <tr key={s.id}>
                      <td>
                        <p className="font-medium text-sm">{s.name || s.email}</p>
                        <p className="text-[11px] text-[var(--color-muted)]">{s.email}</p>
                      </td>
                      <td className="max-w-[160px]">
                        <p className="text-sm truncate">{s.video_title}</p>
                      </td>
                      <td className="text-[var(--color-muted)] text-xs">{new Date(s.purchase_date).toLocaleDateString()}</td>
                      <td className="text-[var(--color-muted)] text-xs">{new Date(s.expiry_date).toLocaleDateString()}</td>
                      <td>
                        <span className={`badge ${s.status === 'active' ? 'badge-green' : 'badge-red'}`}>
                          {s.status === 'active' ? '● Active' : s.status}
                        </span>
                      </td>
                      <td>
                        <div className="flex px-2">
                          {s.status === 'active' ? (
                            <button
                              onClick={() => handleRevoke(s.id)}
                              className="btn btn-sm btn-danger flex items-center gap-1.5"
                            >
                              <Ban className="w-3.5 h-3.5" /> Revoke
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivate(s.id)}
                              className="btn btn-sm btn-ghost flex items-center gap-1.5 border-[var(--color-accent)] text-[var(--color-accent)] hover:bg-[var(--glass-bg)]"
                            >
                              <RefreshCw className="w-3.5 h-3.5" /> Reactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {subs.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-[var(--color-muted)] py-10">No subscriptions yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        onPageChange={fetchSubs}
      />
    </div>
  );
}

export function AdminReports() {
  const { toast } = useToast();

  const handleExport = async (type: string) => {
    let endpoint = '';
    let filename = '';

    switch (type) {
      case 'Subscription Report':
        endpoint = '/admin/subscriptions/export';
        filename = 'subscriptions_export';
        break;
      case 'User Growth Report':
        endpoint = '/admin/users/export';
        filename = 'users_export';
        break;
      default:
        toast('This report is not implemented yet.', 'info');
        return;
    }

    try {
      const res = await api.get(endpoint);
      downloadCSV(res.data, filename);
    } catch {
      toast('Export failed', 'error');
    }
  };

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-black text-[var(--color-text)] tracking-tight">Reports & Analytics</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {['Subscription Report', 'User Growth Report', 'Refund Logs'].map(r => (
          <div key={r} className="card p-5 flex items-center justify-between group hover:border-[var(--color-accent)] transition-colors">
            <div>
              <p className="text-sm font-bold text-[var(--color-text)]">{r}</p>
              <p className="text-xs text-[var(--color-muted)]">Export as CSV or Excel</p>
            </div>
            <button
              onClick={() => handleExport(r)}
              className="btn btn-ghost btn-sm"
            >
              Export
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Investment Queries ────────────────────────────────────────────────────────
export function AdminInvestmentQueries() {
  const [queries, setQueries] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchQueries = (page = 1) => {
    setLoading(true);
    api.get(`/queries/admin?page=${page}&limit=5&search=${search}`)
      .then((res: any) => {
        setQueries(res.data || []);
        setPagination(res.pagination || { page: 1, totalPages: 1, total: 0 });
      })
      .catch(() => setQueries([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchQueries(1);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => fetchQueries(1), 300);
    return () => clearTimeout(handler);
  }, [search]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-black text-[var(--color-text)] tracking-tight">Investment Queries</h1>
          <p className="text-sm text-[var(--color-muted)]">{pagination.total} total queries received</p>
        </div>
        <div className="relative max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
            style={{ color: 'var(--color-muted)' }} />
          <input
            className="input !pl-[30px]"
            placeholder="Search queries…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        <div className="p-4 sm:p-0">
          {/* Mobile card layout */}
          <div className="sm:hidden space-y-3">
            {loading ? (
              <div className="text-center text-sm text-[var(--color-muted)] py-10">Loading queries…</div>
            ) : queries.map((q, i) => (
              <div key={q.id || i} className="bg-[var(--glass-bg)] rounded-xl p-3 border border-[var(--glass-border)]">
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-[var(--color-text)]">{q.name}</p>
                    <p className="text-[10px] text-[var(--color-muted)] truncate">{q.email}</p>
                    <p className="text-[10px] text-[var(--color-muted)] truncate" title={q.remarks}>
                      {q.remarks || '—'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 ml-2">
                    <span className="text-[10px] text-[var(--color-muted)]">#{(pagination.page - 1) * 5 + i + 1}</span>
                    <span className="badge badge-blue text-[10px]">{q.service_type}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[var(--color-muted)]">
                    {new Date(q.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
            {!loading && queries.length === 0 && (
              <div className="text-center text-[var(--color-muted)] py-8 text-sm">No queries found</div>
            )}
          </div>

          {/* Desktop table layout */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th className="w-16">S.No</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Service</th>
                  <th>Remarks</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center text-sm text-[var(--color-muted)] py-10">Loading queries…</td></tr>
                ) : queries.map((q, i) => (
                  <tr key={q.id || i}>
                    <td className="font-mono text-[var(--color-muted)] text-xs">{(pagination.page - 1) * 5 + i + 1}</td>
                    <td className="font-medium">{q.name}</td>
                    <td className="text-sm">{q.email}</td>
                    <td><span className="badge badge-blue">{q.service_type}</span></td>
                    <td className="max-w-[180px] truncate text-[var(--color-muted)] text-xs" title={q.remarks}>
                      {q.remarks || '—'}
                    </td>
                    <td className="text-[var(--color-muted)] text-xs whitespace-nowrap">
                      {new Date(q.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
                {!loading && queries.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-[var(--color-muted)] py-10">No queries found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        onPageChange={fetchQueries}
      />
    </div>
  );
}
